import { useEffect, useRef, useState } from "react";
import { Globe, X } from "lucide-react";
import posthog from "posthog-js";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useLanguageRouter } from "@/hooks/useLanguageRouter";
import { stripLanguagePrefix } from "@/lib/languagePath";
import { pageLanguageForBrowser } from "@shared/pageLanguages";

// Deliberately NOT an auto-redirect: Googlebot and users always get the URL
// they asked for, and switching languages is an explicit one-click choice.
// Strings are hardcoded per-language (not i18next) because the banner must
// render in the *suggested* language while the page itself is still English,
// without pulling that language's ~90KB translation chunk just for one line.
const BANNER_STRINGS: Record<
  string,
  { message: string; accept: string; dismiss: string }
> = {
  es: {
    message: "Este sitio está disponible en español.",
    accept: "Ver en español",
    dismiss: "Seguir en inglés",
  },
  fr: {
    message: "Ce site est disponible en français.",
    accept: "Voir en français",
    dismiss: "Continuer en anglais",
  },
  de: {
    message: "Diese Website ist auf Deutsch verfügbar.",
    accept: "Auf Deutsch ansehen",
    dismiss: "Weiter auf Englisch",
  },
  pt: {
    message: "Este site está disponível em português.",
    accept: "Ver em português",
    dismiss: "Continuar em inglês",
  },
  it: {
    message: "Questo sito è disponibile in italiano.",
    accept: "Vedi in italiano",
    dismiss: "Continua in inglese",
  },
  ja: {
    message: "このサイトは日本語でもご利用いただけます。",
    accept: "日本語で見る",
    dismiss: "英語のまま続ける",
  },
  ko: {
    message: "이 사이트는 한국어로도 이용할 수 있습니다.",
    accept: "한국어로 보기",
    dismiss: "영어로 계속",
  },
  zh: {
    message: "本网站提供简体中文版。",
    accept: "查看中文版",
    dismiss: "继续使用英文",
  },
  id: {
    message: "Situs ini tersedia dalam Bahasa Indonesia.",
    accept: "Lihat dalam Bahasa Indonesia",
    dismiss: "Lanjutkan dalam bahasa Inggris",
  },
  nl: {
    message: "Deze site is beschikbaar in het Nederlands.",
    accept: "Bekijk in het Nederlands",
    dismiss: "Doorgaan in het Engels",
  },
  pl: {
    message: "Ta strona jest dostępna po polsku.",
    accept: "Zobacz po polsku",
    dismiss: "Kontynuuj po angielsku",
  },
  tr: {
    message: "Bu site Türkçe olarak da mevcut.",
    accept: "Türkçe görüntüle",
    dismiss: "İngilizce devam et",
  },
  vi: {
    message: "Trang web này có phiên bản tiếng Việt.",
    accept: "Xem bằng tiếng Việt",
    dismiss: "Tiếp tục bằng tiếng Anh",
  },
};

// Page languages (shared/pageLanguages.ts: Thai, Traditional Chinese) exist
// on a few pages only, so they are offered only on those pages, to browsers
// whose first language matches (th; zh-TW, zh-HK, zh-MO, zh-Hant), and the
// message says "this page", not "this site". They are kept out of
// BANNER_STRINGS on purpose: a language listed there is offered on every
// page.
const PAGE_BANNER_STRINGS: Record<
  string,
  { message: string; accept: string; dismiss: string }
> = {
  th: {
    message: "หน้านี้มีเวอร์ชันภาษาไทย",
    accept: "ดูเป็นภาษาไทย",
    dismiss: "ใช้ภาษาอังกฤษต่อ",
  },
  tw: {
    message: "本頁提供繁體中文版。",
    accept: "以繁體中文檢視",
    dismiss: "繼續使用英文",
  },
};

const STORAGE_KEY = "localeSuggestionChoice";

// Surfaces where a language prompt is noise or the localized route doesn't
// exist (/cli, /skill, /unsubscribe have no /:lang variants; checkout, admin
// and the /mcp-auth app-connection consent step should never be interrupted).
const EXCLUDED_FIRST_SEGMENTS = new Set([
  "admin",
  "checkout",
  "cli",
  "cli-auth",
  "mcp-auth",
  "skill",
  "unsubscribe",
  "404",
]);

function readStoredChoice(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeChoice(value: string) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Private mode / blocked storage: the banner may reappear next visit,
    // which is acceptable.
  }
}

/** The browser's first language as it reports it ("zh-TW", "th-TH"). */
function primaryBrowserLanguage(): string {
  if (typeof navigator === "undefined") return "";
  return navigator.languages?.[0] || navigator.language || "";
}

function detectSuggestedLanguage(): string | null {
  if (typeof navigator === "undefined") return null;
  // QA override: ?lang-suggest=fr forces the banner regardless of browser
  // language, so the flow can be tested without changing OS/browser locale.
  const override = new URLSearchParams(window.location.search).get(
    "lang-suggest",
  );
  if (override && BANNER_STRINGS[override]) return override;
  // Primary browser language only: if someone's first preference is English,
  // suggesting their second-choice language would be presumptuous.
  const primary = (navigator.languages?.[0] || navigator.language || "")
    .slice(0, 2)
    .toLowerCase();
  return BANNER_STRINGS[primary] ? primary : null;
}

export default function LanguageSuggestionBanner() {
  const [location] = useLocation();
  const { i18n } = useTranslation();
  const { changeLanguage } = useLanguageRouter();
  const [hidden, setHidden] = useState(false);
  const shownTrackedRef = useRef(false);

  // Evaluated once per page load: the audience is visitors landing on an
  // unprefixed (English) URL whose browser prefers a language we support.
  const [siteSuggestion] = useState<string | null>(() => {
    if (readStoredChoice()) return null;
    return detectSuggestedLanguage();
  });
  // What a page language is matched against, also read once per page load.
  // Null once a choice is stored, like the suggestion above.
  const [browser] = useState<{ language: string; override: string | null } | null>(
    () =>
      readStoredChoice()
        ? null
        : {
            language: primaryBrowserLanguage(),
            override:
              typeof window === "undefined"
                ? null
                : new URLSearchParams(window.location.search).get(
                    "lang-suggest",
                  ),
          },
  );

  const pathname = location.split(/[?#]/)[0] || "/";
  const firstSegment = pathname.split("/").filter(Boolean)[0] ?? "";
  const pagePath = stripLanguagePrefix(pathname);
  // A URL in one of the 14 site languages, or a page language's own page
  // (/th/ai-music-video-generator): the visitor is already in a language.
  const onUnprefixedUrl = !pagePath.hasLang;
  const onExcludedSurface = EXCLUDED_FIRST_SEGMENTS.has(firstSegment);
  // A page language wins on the pages it has (a zh-TW browser on the music
  // video page is offered Traditional Chinese, not Simplified); everywhere
  // else the site-language suggestion is what it was.
  const pageLanguage =
    browser && onUnprefixedUrl
      ? pageLanguageForBrowser(browser.language, pagePath.pathname, browser.override)
      : null;
  const pageSuggestion =
    pageLanguage && PAGE_BANNER_STRINGS[pageLanguage] ? pageLanguage : null;
  const suggestedLang = pageSuggestion ?? siteSuggestion;

  const visible =
    !!suggestedLang && !hidden && onUnprefixedUrl && !onExcludedSurface;

  useEffect(() => {
    if (visible && !shownTrackedRef.current) {
      shownTrackedRef.current = true;
      posthog.capture("language_suggestion_banner_shown", {
        suggested_language: suggestedLang,
      });
    }
  }, [visible, suggestedLang]);

  if (!visible) return null;

  const strings =
    PAGE_BANNER_STRINGS[suggestedLang] ?? BANNER_STRINGS[suggestedLang];

  const handleAccept = () => {
    posthog.capture("language_suggestion_banner_accepted", {
      suggested_language: suggestedLang,
    });
    storeChoice(`accepted:${suggestedLang}`);
    setHidden(true);
    if (i18n.language !== suggestedLang) {
      i18n.changeLanguage(suggestedLang);
    }
    changeLanguage(suggestedLang);
  };

  const handleDismiss = () => {
    posthog.capture("language_suggestion_banner_dismissed", {
      suggested_language: suggestedLang,
    });
    storeChoice("dismissed");
    setHidden(true);
  };

  return (
    <div className="fixed bottom-20 left-1/2 z-40 w-[calc(100%-2rem)] max-w-[520px] -translate-x-1/2 lg:bottom-6">
      <div
        className="relative flex items-center gap-3 rounded-[14px] py-3 pl-4 pr-10"
        style={{
          background:
            "linear-gradient(150deg, #12131c 0%, #0c0d15 55%, #14101e 100%)",
          border: "1px solid rgba(255,255,255,0.07)",
          boxShadow:
            "0 24px 60px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        <Globe className="h-5 w-5 shrink-0 text-[#9ca3b4]" strokeWidth={1.8} />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-[13px] font-medium text-white">
            {strings.message}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
            <button
              onClick={handleAccept}
              className="text-[13px] font-bold text-[#d946ef] transition-colors hover:text-[#ec4899]"
            >
              {strings.accept}
            </button>
            <button
              onClick={handleDismiss}
              className="text-[12px] font-medium text-[#8a92a6] transition-colors hover:text-white"
            >
              {strings.dismiss}
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="absolute right-2.5 top-2.5 flex h-[28px] w-[28px] items-center justify-center rounded-full text-[#5b6272] transition-colors hover:bg-white/[0.08] hover:text-[#d1d5e0]"
        >
          <X className="h-4 w-4" strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}
